import { MinusCircleOutlined } from "@ant-design/icons";
import { Badge, Button, Table, Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { DataConsumer } from "../../../api";
import {
  useOrganizationDataConsumers,
  useRemoveDataConsumersFromOrganization,
} from "../../../api/api.data-consumers";
import { toAntdPagination, useRefreshAll } from "../../../util";
import { useActiveUserRoles } from "../../../util/useActiveUserRoles";
import { AddDataConsumerButton } from "../actions/AddDataConsumerButton";

interface DataConstumerTableProps {
  organizationId: string;
}

export function DataConsumersTable({
  organizationId,
}: DataConstumerTableProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { isSolutionOperator } = useActiveUserRoles();
  const dataConsumers = useOrganizationDataConsumers(organizationId);

  const refresh = useRefreshAll(dataConsumers);

  const columns = [
    {
      title: t("integrations:data-consumer-name"),
      key: "name",
      sorter: (a: DataConsumer, b: DataConsumer) =>
        a.name.localeCompare(b.name),
      render: (c: DataConsumer) => c.name,
      onCell: isSolutionOperator
        ? (c: DataConsumer) => {
            return {
              onClick: () => {
                history.push(`/integrations/${c.id}`);
              },
              style: { cursor: "pointer" },
            };
          }
        : undefined,
    },
    {
      title: t("beacon:status"),
      key: "status",
      render: (c: DataConsumer) => {
        return c.status === "Approved" ? (
          <Tag color="green">Approved</Tag>
        ) : c.status === "Pending" ? (
          <Tag color="orange">Pending</Tag>
        ) : (
          <Tag color="red">Denied</Tag>
        );
      },
    },
    {
      title: t("beacon:disabled"),
      key: "disabled",
      render: (c: DataConsumer) => {
        return c.disabled ? (
          <Tag color="red">True</Tag>
        ) : (
          <Tag color="green">False</Tag>
        );
      },
    },
  ];

  return (
    <Table
      sticky
      size="middle"
      scroll={{ y: "calc(100vh - 200px)" }}
      dataSource={dataConsumers.value?.items || []}
      rowSelection={{
        type: "checkbox",
        onChange: (selectedRows) => {
          setSelectedIds(selectedRows as string[]);
        },
        selectedRowKeys: selectedIds,
      }}
      loading={dataConsumers.loading}
      rowKey="id"
      columns={columns}
      pagination={toAntdPagination(dataConsumers, false)}
      footer={() => (
        <DataConsumerTableFooter
          currentDataConsumers={dataConsumers.value?.items || []}
          selectedIds={selectedIds}
          organizationId={organizationId}
          setSelectedIds={setSelectedIds}
          refresh={refresh}
        />
      )}
    />
  );
}

interface DataConsumerTableFooterProps {
  currentDataConsumers: DataConsumer[];
  selectedIds: string[];
  organizationId: string;
  setSelectedIds: (ids: string[]) => void;
  refresh: () => void;
}

const DataConsumerTableFooter = ({
  currentDataConsumers,
  selectedIds,
  organizationId,
  setSelectedIds,
  refresh,
}: DataConsumerTableFooterProps) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const removeDataConsumerFromOrganization =
    useRemoveDataConsumersFromOrganization(organizationId);

  const onDeleteDataConsumer = async () => {
    try {
      setIsDeleting(true);
      await removeDataConsumerFromOrganization({
        consumerIds: selectedIds,
      });
      setSelectedIds([]);
      refresh();
      setIsDeleting(false);
    } catch {
      console.error("Failed to delete data consumer");
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto auto auto auto auto auto auto auto 1fr",
        gridColumnGap: "1em",
      }}
    >
      <Badge count={selectedIds.length} color="gray">
        <Button
          disabled={selectedIds.length === 0}
          onClick={() => setSelectedIds([])}
        >
          {t("organization:clear-selected-users")}
        </Button>
      </Badge>
      <Badge count={selectedIds.length}>
        <Button
          danger
          disabled={selectedIds.length === 0}
          loading={isDeleting}
          onClick={onDeleteDataConsumer}
          data-testid="delete-data-consumer-button"
        >
          <MinusCircleOutlined />{" "}
          {t("integrations:action-data-consumer", {
            action: t("translations:remove"),
          })}
        </Button>
      </Badge>
      {organizationId && (
        <AddDataConsumerButton
          organizationId={organizationId}
          currentDataConsumers={currentDataConsumers}
          refresh={refresh}
        />
      )}
    </div>
  );
};
